import mapRepaymentStrategyToDomain from 'modules/cm/api/cases/repaymentStrategy/mapper/apiToDomain';
import { CaseData, ResourcesData } from 'modules/cm/api/cases/types';
import { FIELDS as CURRENT_INCOME_FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { CaseStatuses } from 'modules/cm/domain/Case/CaseStatuses';
import { FIELDS } from 'modules/cm/fields/types';
import currentIncomeMapper from 'modules/cm/mapper/Case/Applicant/Income/Current/mapper';
import futureIncomeMapper, {
  mapDomainToAPI as mapFutureIncomeDomainToApi,
} from 'modules/cm/mapper/Case/Applicant/Income/Future/mapper';
import loanDetailsMapper, { mapLoanDetailsDomainToApi } from 'modules/cm/mapper/Case/LoanDetails/mapper';
import { Admin, CaseDetails, Solicitor } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { BooleanString } from 'shared/interface/globalInterface';
import { format } from 'utils/date';
import { logicalMapBackToFront, logicalMapFrontToBack, stringToBool } from 'utils/form/formUtils';
import { Nullable, notEmpty } from 'utils/types';

type ApplicantKey = 'applicant1' | 'applicant2';
type RawApplicant = NonNullable<CaseData[ApplicantKey]>;
type RawExpenditure = RawApplicant['expenditures'][0];
type RawAsset = RawApplicant['assets'][0];

type AssurancePolicyRaw = CaseData['policies'][0];

type FormCase = CaseDetails['case'];
type FormApplicant = FormCase['applicants'][0];
type FormExpenditure = FormApplicant[FIELDS.EXPENDITURES_PREFIX][0];
type FormAsset = FormApplicant[FIELDS.ASSETS_PREFIX][0];
type FormAssurancePolicy = FormCase[FIELDS.ASSURANCE_POLICIES_PREFIX][0];
type FormSolicitor = FormCase['contact'][FIELDS.SOLICITOR_PREFIX];
type FormAdmin = FormCase['contact'][FIELDS.ADMIN_PREFIX];

type RawFuturePensionDefinedBenefit = RawApplicant['futurePensionDefinedBenefits'][0];
type RawFuturePensionDefinedContribution = RawApplicant['futurePensionDefinedContributions'][0];
type RawFuturePensionAnnuity = RawApplicant['futurePensionAnnuity'][0];

type FormFuturePensionDefinedBenefit = FormApplicant[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX][0];
type FormFuturePensionDefinedContribution = FormApplicant[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX][0];
type FormFuturePensionAnnuity = FormApplicant[FIELDS.FUTURE_ANNUITY_PREFIX][0];

const mapFuturePensionDefinedBenefit = (
  definedBenefitRawData: RawFuturePensionDefinedBenefit,
): FormFuturePensionDefinedBenefit => {
  return {
    ...definedBenefitRawData,
    startDate: definedBenefitRawData.startDate
      ? format(definedBenefitRawData.startDate).fromBackendToView()
      : definedBenefitRawData.startDate,
  };
};

const mapFuturePensionDefinedBenefitToApi = (
  definedBenefitFormData: FormFuturePensionDefinedBenefit,
): RawFuturePensionDefinedBenefit => {
  return {
    ...definedBenefitFormData,
    startDate: definedBenefitFormData.startDate
      ? format(definedBenefitFormData.startDate).fromViewToRequest(true)
      : definedBenefitFormData.startDate,
  };
};

const mapFuturePensionDefinedContribution = (
  definedContributionRawData: RawFuturePensionDefinedContribution,
): FormFuturePensionDefinedContribution => {
  return {
    ...definedContributionRawData,
    startDate: definedContributionRawData.startDate
      ? format(definedContributionRawData.startDate).fromBackendToView()
      : definedContributionRawData.startDate,
  };
};

const mapFuturePensionDefinedContributionToApi = (
  definedContributionFormData: FormFuturePensionDefinedContribution,
): RawFuturePensionDefinedContribution => {
  return {
    ...definedContributionFormData,
    startDate: definedContributionFormData.startDate
      ? format(definedContributionFormData.startDate).fromViewToRequest(true)
      : definedContributionFormData.startDate,
  };
};

const mapFutureAnnuity = (futureAnnuityRawData: RawFuturePensionAnnuity): FormFuturePensionAnnuity => {
  return {
    ...futureAnnuityRawData,
  };
};

const mapFutureAnnuityToApi = (futureAnnuityFormData: FormFuturePensionAnnuity): RawFuturePensionAnnuity => {
  return {
    ...futureAnnuityFormData,
  };
};

export const fromApiMapper = {
  mapFuturePensionDefinedBenefit,
  mapFuturePensionDefinedContribution,
  mapFutureAnnuity,
};

export const caseFromApi = (rawData: CaseData): CaseDetails['case'] => {
  const mapApplicant = (applicantRawData: RawApplicant): FormApplicant => {
    const mapAsset = (assetRawData: RawAsset): FormAsset => {
      return {
        [FIELDS.ASSETS_ID]: assetRawData.id,
        [FIELDS.ASSETS_TYPE]: assetRawData.type,
        [FIELDS.ASSETS_VALUE]: assetRawData.value,
        [FIELDS.ASSETS_DESCRIPTION]: assetRawData.description,
        [FIELDS.ASSETS_WILL_BE_USED_TO_PAY_LOAN]: assetRawData.willBeUsedToPayLoan,
        [FIELDS.ASSETS_OUTSTANDING_MORTGAGE_BALANCE]: assetRawData.outstandingMortgageBalance,
        [FIELDS.ASSETS_PROPERTY_POSTCODE]: assetRawData.postcode,
        [FIELDS.ASSETS_PROPERTY_CITY]: assetRawData.city,
        [FIELDS.ASSETS_PROPERTY_FLAT_NUMBER]: assetRawData.flatNumber,
        [FIELDS.ASSETS_PROPERTY_BUILDING_NAME]: assetRawData.buildingName,
        [FIELDS.ASSETS_PROPERTY_BUILDING_NUMBER]: assetRawData.buildingNumber,
        [FIELDS.ASSETS_PROPERTY_STREET]: assetRawData.street,
      };
    };

    const mapLiability = (expenditureRawData: RawExpenditure): FormExpenditure => {
      return {
        ...expenditureRawData,
        [FIELDS.EXPENDITURE_END_DATE]: expenditureRawData.endDate
          ? format(expenditureRawData.endDate).fromBackendToView()
          : expenditureRawData.endDate,
      };
    };

    return {
      id: applicantRawData.id,
      name: applicantRawData.firstName,
      surname: applicantRawData.lastName,
      title: applicantRawData.title,
      [FIELDS.EXPENDITURES_PREFIX]: applicantRawData.expenditures.map(mapLiability),
      assets: applicantRawData.assets.map(mapAsset),
      [FIELDS.APPLICANT_MIDDLE_NAME]: applicantRawData.middleName,
      [FIELDS.APPLICANT_PREVIOUS_NAME]: applicantRawData.previousName,
      [FIELDS.APPLICANT_PROOF_OF_ID]: applicantRawData.proofOfId,
      [FIELDS.APPLICANT_DATE_OF_BIRTH]: applicantRawData.dateOfBirth
        ? format(applicantRawData.dateOfBirth).fromBackendToView()
        : '',
      [FIELDS.APPLICANT_GENDER]: applicantRawData.gender,
      [FIELDS.APPLICANT_MARITAL_STATUS]: applicantRawData.maritalStatus,
      [FIELDS.APPLICANT_CURRENT_ADDRESS]: {
        id: applicantRawData.addresses[0].id,
        [FIELDS.APPLICANT_ADDRESS_FLAT_NUMBER]: applicantRawData.addresses[0].flatNumber,
        [FIELDS.APPLICANT_ADDRESS_BUILDING_NAME]: applicantRawData.addresses[0].buildingName,
        [FIELDS.APPLICANT_ADDRESS_BUILDING_NUMBER]: applicantRawData.addresses[0].buildingNumber,
        [FIELDS.APPLICANT_ADDRESS_STREET]: applicantRawData.addresses[0].street,
        [FIELDS.APPLICANT_ADDRESS_CITY]: applicantRawData.addresses[0].city,
        [FIELDS.APPLICANT_ADDRESS_POSTCODE]: applicantRawData.addresses[0].postcode,
        [FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE]: applicantRawData.addresses[0].movedInDate
          ? format(applicantRawData.addresses[0].movedInDate).fromBackendToView()
          : '',
        [FIELDS.APPLICANT_ADDRESS_PROOF_OF_ADDRESS]: applicantRawData.addresses[0].proofOfAddress,
      },
      [FIELDS.APPLICANT_PREVIOUS_ADDRESSES]: applicantRawData.addresses.slice(1).map((address) => ({
        id: address.id,
        [FIELDS.APPLICANT_ADDRESS_FLAT_NUMBER]: address.flatNumber,
        [FIELDS.APPLICANT_ADDRESS_BUILDING_NAME]: address.buildingName,
        [FIELDS.APPLICANT_ADDRESS_BUILDING_NUMBER]: address.buildingNumber,
        [FIELDS.APPLICANT_ADDRESS_STREET]: address.street,
        [FIELDS.APPLICANT_ADDRESS_CITY]: address.city,
        [FIELDS.APPLICANT_ADDRESS_POSTCODE]: address.postcode,
        [FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE]: address.movedInDate
          ? format(address.movedInDate).fromBackendToView()
          : '',
        [FIELDS.APPLICANT_ADDRESS_PROOF_OF_ADDRESS]: applicantRawData.addresses[0].proofOfAddress,
      })),
      [FIELDS.APPLICANT_NATIONALITY]: applicantRawData.nationality,
      [FIELDS.APPLICANT_IS_UK_RESIDENT]: applicantRawData.isUkResident,
      [FIELDS.APPLICANT_HAS_INDEFINITE_LEAVE_TO_REMAIN]: applicantRawData.hasIndefiniteLeaveToRemain,
      [FIELDS.APPLICANT_PHONE_HOME]: applicantRawData.phoneHome,
      [FIELDS.APPLICANT_IS_PHONE_HOME_PEPPER]: applicantRawData.isPhoneHomePepper,
      [FIELDS.APPLICANT_PHONE_MOBILE]: applicantRawData.phoneMobile,
      [FIELDS.APPLICANT_IS_PHONE_MOBILE_PEPPER]: applicantRawData.isPhoneMobilePepper,
      [FIELDS.APPLICANT_PHONE_WORK]: applicantRawData.phoneWork,
      [FIELDS.APPLICANT_IS_PHONE_WORK_PEPPER]: applicantRawData.isPhoneWorkPepper,
      [FIELDS.APPLICANT_EMAIL]: applicantRawData.email,
      [FIELDS.APPLICANT_IS_EMAIL_PEPPER]: applicantRawData.isEmailPepper,
      [FIELDS.APPLICANT_ADDITIONAL_EMAIL]: applicantRawData.additionalEmail,
      [FIELDS.APPLICANT_ADDITIONAL_EMAIL_PEPPER]: applicantRawData.isAdditionalEmailPepper,
      [FIELDS.APPLICANT_IS_CRIMINAL]:
        typeof applicantRawData.isCriminal === 'boolean' ? logicalMapBackToFront(applicantRawData.isCriminal) : null,
      [FIELDS.APPLICANT_CRIMINAL_CONVICTIONS_DETAIL]: applicantRawData.criminalConvictionsDetails,
      [FIELDS.APPLICANT_HAS_ADVERSE_CREDIT]:
        typeof applicantRawData.hasAdverseCredit === 'boolean'
          ? logicalMapBackToFront(applicantRawData.hasAdverseCredit)
          : null,
      [FIELDS.APPLICANT_ADVERSE_CREDIT_DETAILS]: applicantRawData.adverseCreditDetails,
      [FIELDS.APPLICANT_BANKRUPTCY_AMOUNT]: applicantRawData.bankruptcyAmount,
      [FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE]: applicantRawData.bankruptcyLatestDate
        ? format(applicantRawData.bankruptcyLatestDate).fromBackendToView()
        : '',
      [FIELDS.APPLICANT_CCJ_AMOUNT]: applicantRawData.ccjAmount,
      [FIELDS.APPLICANT_CCJ_LATEST_DATE]: applicantRawData.ccjLatestDate
        ? format(applicantRawData.ccjLatestDate).fromBackendToView()
        : '',
      [FIELDS.APPLICANT_DEFAULT_AMOUNT]: applicantRawData.defaultAmount,
      [FIELDS.APPLICANT_DEFAULT_LAST_DATE]: applicantRawData.defaultLatestDate
        ? format(applicantRawData.defaultLatestDate).fromBackendToView()
        : '',
      [FIELDS.APPLICANT_MORTGAGE_ARREARS_AMOUNT]: applicantRawData.mortgageArrearsAmount,
      [FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE]: applicantRawData.mortgageArrearsLatestDate
        ? format(applicantRawData.mortgageArrearsLatestDate).fromBackendToView()
        : '',
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY]: {
        id: applicantRawData.powerOfAttorney.id,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_NAME]: applicantRawData.powerOfAttorney.name,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_PHONE_NUMBER]: applicantRawData.powerOfAttorney.phoneNumber,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_EMAIL]: applicantRawData.powerOfAttorney.email,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_POSTCODE]: applicantRawData.powerOfAttorney.postcode,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_FLAT_NUMBER]: applicantRawData.powerOfAttorney.flatNumber,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NAME]: applicantRawData.powerOfAttorney.buildingName,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NUMBER]: applicantRawData.powerOfAttorney.buildingNumber,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_STREET]: applicantRawData.powerOfAttorney.street,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_CITY]: applicantRawData.powerOfAttorney.city,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_HAS_CUSTOMISED_SERVICES]: applicantRawData.hasCustomisedServices
          ? Boolean(applicantRawData.hasCustomisedServices)
          : null,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_CUSTOMISED_SERVICES_REQUEST]: applicantRawData.customisedServicesRequest,
      },
      [FIELDS.CURRENT_STATE_PENSION]: applicantRawData.currentStatePension,
      [FIELDS.CURRENT_DB_NAME_OF_SCHEME]: applicantRawData.currentDbNameOfScheme,
      [FIELDS.CURRENT_DB_ANNUAL_INCOME]: applicantRawData.currentDbAnnualIncome,
      [FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH]: applicantRawData.currentDbPercentPassedOnDeath,
      [FIELDS.CURRENT_DC_NAME_OF_SCHEME]: applicantRawData.currentDcNameOfScheme,
      [FIELDS.CURRENT_DC_CURRENT_VALUE]: applicantRawData.currentDcCurrentValue,
      [FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH]: applicantRawData.currentDcPercentPassedOnDeath,
      [FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN]: applicantRawData.currentDcAnnualIncomeDrawn,
      [FIELDS.CURRENT_ANNUITY_NAME_OF_ANNUITY_PROVIDER]: applicantRawData.currentAnnuityNameOfAnnuityProvider,
      [FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME]: applicantRawData.currentAnnuityGrossAnnualIncome,
      [FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH]: applicantRawData.currentAnnuityPercentPassedOnDeath,
      [FIELDS.FUTURE_STATE_PENSION]: applicantRawData.futureStatePension,
      [FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX]: applicantRawData.futurePensionDefinedBenefits.map(
        mapFuturePensionDefinedBenefit,
      ),
      [FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX]: applicantRawData.futurePensionDefinedContributions.map(
        mapFuturePensionDefinedContribution,
      ),
      [FIELDS.FUTURE_ANNUITY_PREFIX]: applicantRawData.futurePensionAnnuity.map(mapFutureAnnuity),
      [FIELDS.APPLICANT_EMPLOYMENT_STATUS]: applicantRawData.employmentStatus,
      [FIELDS.APPLICANT_EMPLOYED_JOB_TITLE]: applicantRawData.employedJobTitle,
      [FIELDS.APPLICANT_EMPLOYED_COMPANY_NAME]: applicantRawData.employedCompanyName,
      [FIELDS.APPLICANT_EMPLOYED_COMPANY_ADDRESS]: applicantRawData.employedCompanyAddress,
      [FIELDS.APPLICANT_EMPLOYED_NATURE]: applicantRawData.employedNature,
      [FIELDS.APPLICANT_EMPLOYED_START_DATE]: applicantRawData.employedStartDate
        ? format(applicantRawData.employedStartDate).fromBackendToView()
        : '',
      [FIELDS.APPLICANT_EMPLOYED_ANTICIPATED_AGE]: applicantRawData.employedAnticipatedAge,
      [FIELDS.APPLICANT_SELF_EMPLOYED_JOB_TITLE]: applicantRawData.selfEmployedJobTitle,
      [FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_NAME]: applicantRawData.selfEmployedCompanyName,
      [FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_ADDRESS]: applicantRawData.selfEmployedCompanyAddress,
      [FIELDS.APPLICANT_SELF_EMPLOYED_NATURE]: applicantRawData.selfEmployedNature,
      [FIELDS.APPLICANT_SELF_EMPLOYED_START_DATE]: applicantRawData.selfEmployedStartDate
        ? format(applicantRawData.selfEmployedStartDate).fromBackendToView()
        : '',
      [FIELDS.APPLICANT_SELF_EMPLOYED_ANTICIPATED_AGE]: applicantRawData.selfEmployedAnticipatedAge,
      ...currentIncomeMapper.mapApiToDomain(applicantRawData),
      ...futureIncomeMapper.mapApiToDomain(applicantRawData),
      [FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS]: applicantRawData.numberOfDependants,
      [FIELDS.LUMP_SUM_DRAWDOWN]: applicantRawData.lumpSumDrawdown,
      [FIELDS.REPAYMENT_LUMP_SUM]: applicantRawData.repaymentLumpSum,
    };
  };

  const mapProperty = (caseData: CaseData): CaseDetails['case']['property'] => {
    return {
      ...caseData.property,
      [FIELDS.PROPERTY_INSTRUCTION_DATE]: caseData.property.instructionDate
        ? format(caseData.property.instructionDate).fromBackendToView()
        : '',
      [FIELDS.PROPERTY_INSPECTION_DATE]: caseData.property.inspectionDate
        ? format(caseData.property.inspectionDate).fromBackendToView()
        : '',
      [FIELDS.PROPERTY_IS_PROPERTY_NEW_BUILD]:
        typeof caseData.property.isPropertyNewBuild === 'boolean'
          ? logicalMapBackToFront(caseData.property.isPropertyNewBuild)
          : null,
      [FIELDS.PROPERTY_HAS_SPECIALIST_REPORT]:
        typeof caseData.property.hasSpecialistReport === 'boolean'
          ? logicalMapBackToFront(caseData.property.hasSpecialistReport)
          : null,
      [FIELDS.PROPERTY_HAS_COMMERCIAL_USAGE_ABOVE25_PERCENT]:
        typeof caseData.property.hasCommercialUsageAbove25Percent === 'boolean'
          ? logicalMapBackToFront(caseData.property.hasCommercialUsageAbove25Percent)
          : null,
      [FIELDS.PROPERTY_HAS_LIFT_ACCESS]:
        typeof caseData.property.hasLiftAccess === 'boolean'
          ? logicalMapBackToFront(caseData.property.hasLiftAccess)
          : null,
      [FIELDS.PROPERTY_IS_PRIMARY_HOME]:
        typeof caseData.property.isPrimaryHome === 'boolean'
          ? logicalMapBackToFront(caseData.property.isPrimaryHome)
          : null,
      [FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE]: caseData.estimatedPropertyValue,
      [FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE]: caseData.propertyPurchasePrice,
      [FIELDS.PROPERTY_ACTUAL_PROPERTY_VALUE]: caseData.actualPropertyValue,
      [FIELDS.PROPERTY_OCCUPANTS_PREFIX]: caseData.property.occupants.map((occupant) => ({
        ...occupant,
        [FIELDS.PROPERTY_OCCUPANT_DATE_OF_BIRTH]: occupant.dateOfBirth
          ? format(occupant.dateOfBirth).fromBackendToView()
          : '',
      })),
    };
  };

  const mapAssurancePolicy = (assurancePolicyRaw: AssurancePolicyRaw): FormAssurancePolicy => {
    return {
      ...assurancePolicyRaw,
      [FIELDS.ASSURANCE_POLICY_EXPIRY_DATE]: assurancePolicyRaw.expiryDate
        ? format(assurancePolicyRaw.expiryDate).fromBackendToView()
        : assurancePolicyRaw.expiryDate,
    };
  };

  const mapSolicitor = (rawData: CaseData): FormSolicitor => ({
    [FIELDS.SOLICITOR_NAME]: rawData.solicitorName,
    [FIELDS.SOLICITOR_CONTACT_NUMBER]: rawData.solicitorContactNumber,
    [FIELDS.SOLICITOR_EMAIL]: rawData.solicitorEmail,
    [FIELDS.SOLICITOR_FLAT_NUMBER]: rawData.solicitorFlatNumber,
    [FIELDS.SOLICITOR_BUILDING_NUMBER]: rawData.solicitorBuildingNumber,
    [FIELDS.SOLICITOR_BUILDING_NAME]: rawData.solicitorBuildingName,
    [FIELDS.SOLICITOR_STREET]: rawData.solicitorStreet,
    [FIELDS.SOLICITOR_ADDRESS_CITY]: rawData.solicitorCity,
    [FIELDS.SOLICITOR_ADDRESS_POSTCODE]: rawData.solicitorPostcode,
  });

  const mapAdmin = (rawData: CaseData): FormAdmin => ({
    [FIELDS.ADMIN_OFFER_DATE]: rawData.offerDate ? format(rawData.offerDate).fromBackendToView() : '',
    [FIELDS.ADMIN_COT_REC_DATE]: rawData.cotRecDate ? format(rawData.cotRecDate).fromBackendToView() : '',
    [FIELDS.ADMIN_FUNDS_REQ_DATE]: rawData.fundsReqDate ? format(rawData.fundsReqDate).fromBackendToView() : '',
    [FIELDS.ADMIN_EXPECTED_COMPLETION_DATE]: rawData.expectedCompletionDate
      ? format(rawData.expectedCompletionDate).fromBackendToView()
      : '',
    [FIELDS.ADMIN_COMPLETION_DATE]: rawData.completionDate ? format(rawData.completionDate).fromBackendToView() : '',
    [FIELDS.ADMIN_PEPPER_UPLOAD_DATE]: rawData.pepperUploadDate
      ? format(rawData.pepperUploadDate).fromBackendToViewWithHoursAndMinutes()
      : '',
    [FIELDS.ADMIN_PEPPER_ACCOUNT_ID]: rawData.pepperAccountId,
    [FIELDS.ADMIN_ORIGINATOR_ID]: rawData.originatorId,
    [FIELDS.ADMIN_DECLINE_DATE]: rawData.declineDate ? format(rawData.declineDate).fromBackendToView() : '',
    [FIELDS.ADMIN_SWAP_COST]: rawData.swapCost,
    [FIELDS.ADMIN_EXPECTED_MATURITY]: rawData.expectedMaturity,
  });

  return {
    status: rawData.status || CaseStatuses.NotSet,
    applicants: [rawData.applicant1, rawData.applicant2].filter(notEmpty).map(mapApplicant),
    contact: {
      [FIELDS.ADVISER_NAME]: rawData.adviserName,
      [FIELDS.ADVISER_FEE]: rawData.adviserFee,
      [FIELDS.ADVISER_PHONE]: rawData.adviserPhone,
      [FIELDS.ADVISER_EMAIL]: rawData.adviserEmail,
      [FIELDS.STANDARD_COMMISSION_TERMS]: rawData.standardCommissionTerms,
      [FIELDS.ENHANCED_COMMISSION_TERMS]: rawData.enhancedCommissionTerms,
      [FIELDS.ONGOING_COMMISSION_TERMS_UPFRONT_FEE]: rawData.ongoingCommissionTermsUpfrontFee,
      [FIELDS.ONGOING_COMMISSION_TERMS_ONGOING_FEE]: rawData.ongoingCommissionTermsOngoingFee,
      [FIELDS.FIRM_NAME]: rawData.firmName,
      [FIELDS.FIRM_FCA_NUMBER]: rawData.firmFcaNumber,
      [FIELDS.FIRM_ADDRESS]: rawData.firmAddress,
      [FIELDS.NETWORK_FCA_NUMBER]: rawData.networkFcaNumber,
      [FIELDS.ADVISER_FEE_LAST_PAYMENT_MOMENT]: rawData.adviserFeeLastPaymentMoment,
      [FIELDS.PACKAGER_FEE]: rawData.packagerFee,
      [FIELDS.PACKAGER_FEE_LAST_PAYMENT_MOMENT]: rawData.packagerFeeLastPaymentMoment,
      [FIELDS.SOLICITOR_PREFIX]: mapSolicitor(rawData),
      [FIELDS.ADMIN_PREFIX]: mapAdmin(rawData),
    },
    rationale: rawData.rationale,
    offerPackUrl: rawData.offerPackUrl,
    preOfferEsisUrl: rawData.preOfferEsisUrl,
    identifier: rawData.caseId,
    loanDetails: loanDetailsMapper.mapApiToDomain(rawData),
    property: mapProperty(rawData),
    repaymentStrategy: rawData.repaymentStrategy ? mapRepaymentStrategyToDomain(rawData.repaymentStrategy) : null,
    updatedAt: rawData.updatedAt ? rawData.updatedAt : '',
    [FIELDS.ASSURANCE_POLICIES_PREFIX]: rawData.policies.map(mapAssurancePolicy),
  };
};

export const resourcesFromApi = (rawData: ResourcesData): CaseDetails['resources'] => {
  return {
    gender: rawData.gender.map((resource) => ({
      option: resource.option,
      value: resource.value,
    })),
    applicationTypes: rawData.applicationType.map((resource) => ({
      option: resource.option,
      value: resource.value,
    })),
    caseStatuses: rawData.caseStatus,
    creditPolicyExemptiones: rawData.creditPolicyExemption,
    pepperPurposesOfTheLoan: rawData.loanPurposePepper,
    productFeePaymentMethods: rawData.productFeePaymentMethod,
    purposesOfTheLoan: rawData.purposeOfTheLoan,
    sourcesOfDeposit: rawData.sourceOfDeposit,
    valuationFees: rawData.valuationFee,
    underwriters: rawData.underwriters,
    creditFeePayMoment: rawData.creditFeePayMoment.map((resource) => ({
      option: resource.option,
      value: resource.value,
    })),
    originatorIds: rawData.originatorIds.map((resource) => ({
      option: resource.option.toUpperCase(),
      value: resource.value,
    })),
    title: rawData.title,
    proofOfAddress: rawData.proofOfAddress,
    proofOfId: rawData.proofOfId,
    martialStatus: rawData.martialStatus,
    assetTypes: rawData.assetTypes,
    products: rawData.products,
    expenditurePaymentFrequencies: rawData.expenditurePaymentFrequencies,
    expenditureTypes: rawData.expenditureTypes,
    nationalities: rawData.nationalities,
    useOfFunds: rawData.useOfFund,
    employmentStatuses: rawData.employmentStatuses,
    employmentNatures: rawData.employmentNatures,
    businessTypes: rawData.businessTypes,
    propertyTypes: rawData.propertyTypes,
    tenureTypes: rawData.tenureTypes,
    propertyConstructions: rawData.propertyConstructions,
    valuationTypes: rawData.valuationTypes,
    assurancePolicyTypes: rawData.policyTypes,
    assuredPersons: rawData.assuredPersons,
    basisCovers: rawData.basisCovers,
    fcaLoanPurposes: rawData.fcaLoanPurposes,
    existingMortgageStatuses: rawData.existingMortgageStatuses,
    authorityLevels: rawData.authorityLevels,
    vulnerabilityCategories: rawData.vulnerabilityCategories,
    informationProvidedBy: rawData.informationProvidedBy,
    productTermLengths: rawData.productTermLengths,
    tiers: rawData.tiers,
  };
};

export const fieldNameToApi = (field: string): string => {
  const map: Map<string, string> = new Map([[FIELDS.MONTHLY_BASIC_SPEND, 'monthlyBasicSpend']]);

  const mapper = map.get(field);

  return mapper ? mapper : field;
};

export const applicantFieldNameToApi = (field: string): string => {
  const map: Map<string, string> = new Map([
    ['name', 'firstName'],
    ['surname', 'lastName'],
  ]);

  const mapper = map.get(field);

  return mapper ? mapper : field;
};

const mapDate = (value?: string): string | null => {
  return value ? format(value).fromViewToRequest(true) : null;
};

const mapNumber = (value?: string | number): Nullable<number> => {
  return value ? Number(value) : null;
};

const mapYesNoSelect = (value: string): boolean | null => {
  return value === '' ? null : stringToBool(value);
};
const mapString = (value: string): string | null => {
  return value === '' ? null : value;
};

export const mapCaseToApi = (formData: CaseDetails['case']): CaseData => {
  const mapApplicantToApi = (applicantFormData: FormApplicant): RawApplicant => {
    const mapAssetToApi = (assetFormData: FormAsset): RawAsset => {
      return {
        id: assetFormData[FIELDS.ASSETS_ID],
        type: assetFormData[FIELDS.ASSETS_TYPE],
        value: assetFormData[FIELDS.ASSETS_VALUE],
        description: assetFormData[FIELDS.ASSETS_DESCRIPTION],
        willBeUsedToPayLoan: assetFormData[FIELDS.ASSETS_WILL_BE_USED_TO_PAY_LOAN],
        outstandingMortgageBalance: assetFormData[FIELDS.ASSETS_OUTSTANDING_MORTGAGE_BALANCE],
        postcode: assetFormData[FIELDS.ASSETS_PROPERTY_POSTCODE],
        city: assetFormData[FIELDS.ASSETS_PROPERTY_CITY],
        flatNumber: assetFormData[FIELDS.ASSETS_PROPERTY_FLAT_NUMBER],
        buildingName: assetFormData[FIELDS.ASSETS_PROPERTY_BUILDING_NAME],
        buildingNumber: assetFormData[FIELDS.ASSETS_PROPERTY_BUILDING_NUMBER],
        street: assetFormData[FIELDS.ASSETS_PROPERTY_STREET],
      };
    };

    const mapLiabilityToApi = (expenditureFormData: FormExpenditure): RawExpenditure => {
      return {
        id: expenditureFormData[FIELDS.EXPENDITURE_ID],
        type: expenditureFormData[FIELDS.EXPENDITURE_TYPE],
        creditLimit: expenditureFormData[FIELDS.EXPENDITURE_CREDIT_LIMIT],
        balance: expenditureFormData[FIELDS.EXPENDITURE_BALANCE],
        description: expenditureFormData[FIELDS.EXPENDITURE_DESCRIPTION],
        amount: expenditureFormData[FIELDS.EXPENDITURE_AMOUNT],
        frequency: expenditureFormData[FIELDS.EXPENDITURE_FREQUENCY],
        totalNumberOfProperties: expenditureFormData[FIELDS.EXPENDITURE_TOTAL_NUMBER_OF_PROPERTIES],
        willBeClearedBeforeLoan: expenditureFormData[FIELDS.EXPENDITURE_WILL_BE_CLEARED_BEFORE_LOAN],
        willBeClearedByLoan: expenditureFormData[FIELDS.EXPENDITURE_WILL_BE_CLEARED_BY_LOAN],
        endDate: expenditureFormData[FIELDS.EXPENDITURE_END_DATE]
          ? format(expenditureFormData[FIELDS.EXPENDITURE_END_DATE] || '').fromViewToRequest(true)
          : null,
      };
    };

    const mapAddressToApi = (address: any) => {
      return {
        id: address.id,
        flatNumber: address[FIELDS.APPLICANT_ADDRESS_FLAT_NUMBER],
        buildingName: address[FIELDS.APPLICANT_ADDRESS_BUILDING_NAME],
        buildingNumber: address[FIELDS.APPLICANT_ADDRESS_BUILDING_NUMBER],
        street: address[FIELDS.APPLICANT_ADDRESS_STREET],
        city: address[FIELDS.APPLICANT_ADDRESS_CITY],
        postcode: address[FIELDS.APPLICANT_ADDRESS_POSTCODE],
        movedInDate: address[FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE]
          ? format(address[FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE] || '').fromViewToRequest(true)
          : null,
        proofOfAddress: address[FIELDS.APPLICANT_ADDRESS_PROOF_OF_ADDRESS],
      };
    };

    return {
      id: applicantFormData.id,
      firstName: applicantFormData.name,
      lastName: applicantFormData.surname,
      title: applicantFormData.title,
      expenditures: applicantFormData[FIELDS.EXPENDITURES_PREFIX].map(mapLiabilityToApi),
      assets: applicantFormData[FIELDS.ASSETS_PREFIX].map(mapAssetToApi),
      middleName: applicantFormData[FIELDS.APPLICANT_MIDDLE_NAME],
      previousName: applicantFormData[FIELDS.APPLICANT_PREVIOUS_NAME],
      proofOfId: applicantFormData[FIELDS.APPLICANT_PROOF_OF_ID],
      dateOfBirth: applicantFormData[FIELDS.APPLICANT_DATE_OF_BIRTH]
        ? format(applicantFormData[FIELDS.APPLICANT_DATE_OF_BIRTH] || '').fromViewToRequest(true)
        : '',
      gender: applicantFormData[FIELDS.APPLICANT_GENDER],
      maritalStatus: applicantFormData[FIELDS.APPLICANT_MARITAL_STATUS],
      addresses: [
        mapAddressToApi(applicantFormData.currentAddress),
        ...applicantFormData[FIELDS.APPLICANT_PREVIOUS_ADDRESSES].map((address) => mapAddressToApi(address)),
      ],
      nationality: applicantFormData[FIELDS.APPLICANT_NATIONALITY],
      isUkResident: applicantFormData[FIELDS.APPLICANT_IS_UK_RESIDENT],
      hasIndefiniteLeaveToRemain: applicantFormData[FIELDS.APPLICANT_HAS_INDEFINITE_LEAVE_TO_REMAIN],
      phoneHome: applicantFormData[FIELDS.APPLICANT_PHONE_HOME],
      isPhoneHomePepper: applicantFormData[FIELDS.APPLICANT_IS_PHONE_HOME_PEPPER],
      phoneMobile: applicantFormData[FIELDS.APPLICANT_PHONE_MOBILE],
      isPhoneMobilePepper: applicantFormData[FIELDS.APPLICANT_IS_PHONE_MOBILE_PEPPER],
      phoneWork: applicantFormData[FIELDS.APPLICANT_PHONE_WORK],
      isPhoneWorkPepper: applicantFormData[FIELDS.APPLICANT_IS_PHONE_WORK_PEPPER],
      email: applicantFormData[FIELDS.APPLICANT_EMAIL],
      isEmailPepper: applicantFormData[FIELDS.APPLICANT_IS_EMAIL_PEPPER],
      additionalEmail: applicantFormData[FIELDS.APPLICANT_ADDITIONAL_EMAIL],
      isAdditionalEmailPepper: applicantFormData[FIELDS.APPLICANT_ADDITIONAL_EMAIL_PEPPER],
      isCriminal: applicantFormData[FIELDS.APPLICANT_IS_CRIMINAL]
        ? logicalMapFrontToBack(applicantFormData[FIELDS.APPLICANT_IS_CRIMINAL] as BooleanString)
        : null,
      criminalConvictionsDetails: applicantFormData[FIELDS.APPLICANT_CRIMINAL_CONVICTIONS_DETAIL],
      hasAdverseCredit: applicantFormData[FIELDS.APPLICANT_HAS_ADVERSE_CREDIT]
        ? logicalMapFrontToBack(applicantFormData[FIELDS.APPLICANT_HAS_ADVERSE_CREDIT] as BooleanString)
        : null,
      adverseCreditDetails: applicantFormData[FIELDS.APPLICANT_ADVERSE_CREDIT_DETAILS],
      bankruptcyAmount: applicantFormData[FIELDS.APPLICANT_BANKRUPTCY_AMOUNT],
      bankruptcyLatestDate: applicantFormData[FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE]
        ? format(applicantFormData[FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE] || '').fromViewToRequest(true)
        : null,
      ccjAmount: applicantFormData[FIELDS.APPLICANT_CCJ_AMOUNT],
      ccjLatestDate: applicantFormData[FIELDS.APPLICANT_CCJ_LATEST_DATE]
        ? format(applicantFormData[FIELDS.APPLICANT_CCJ_LATEST_DATE] || '').fromViewToRequest(true)
        : null,
      defaultAmount: applicantFormData[FIELDS.APPLICANT_DEFAULT_AMOUNT],
      defaultLatestDate: applicantFormData[FIELDS.APPLICANT_DEFAULT_LAST_DATE]
        ? format(applicantFormData[FIELDS.APPLICANT_DEFAULT_LAST_DATE] || '').fromViewToRequest(true)
        : null,
      mortgageArrearsAmount: applicantFormData[FIELDS.APPLICANT_MORTGAGE_ARREARS_AMOUNT],
      mortgageArrearsLatestDate: applicantFormData[FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE]
        ? format(applicantFormData[FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE] || '').fromViewToRequest(true)
        : null,
      hasCustomisedServices:
        applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_HAS_CUSTOMISED_SERVICES],
      customisedServicesRequest:
        applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_CUSTOMISED_SERVICES_REQUEST],
      [FIELDS.APPLICANT_POWER_OF_ATTORNEY]: {
        id: applicantFormData.powerOfAttorney.id,
        name: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_NAME],
        phoneNumber: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_PHONE_NUMBER],
        email: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_EMAIL],
        postcode: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_POSTCODE],
        flatNumber: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_FLAT_NUMBER],
        buildingName: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NAME],
        buildingNumber: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_BUILDING_NUMBER],
        street: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_STREET],
        city: applicantFormData.powerOfAttorney[FIELDS.APPLICANT_POWER_OF_ATTORNEY_CITY],
      },
      currentStatePension: applicantFormData[FIELDS.CURRENT_STATE_PENSION],
      currentDbNameOfScheme: applicantFormData[FIELDS.CURRENT_DB_NAME_OF_SCHEME],
      currentDbAnnualIncome: applicantFormData[FIELDS.CURRENT_DB_ANNUAL_INCOME],
      currentDbPercentPassedOnDeath: applicantFormData[FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH],
      currentDcNameOfScheme: applicantFormData[FIELDS.CURRENT_DC_NAME_OF_SCHEME],
      currentDcCurrentValue: applicantFormData[FIELDS.CURRENT_DC_CURRENT_VALUE],
      currentDcPercentPassedOnDeath: applicantFormData[FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH],
      currentDcAnnualIncomeDrawn: applicantFormData[FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN],
      currentAnnuityNameOfAnnuityProvider: applicantFormData[FIELDS.CURRENT_ANNUITY_NAME_OF_ANNUITY_PROVIDER],
      currentAnnuityGrossAnnualIncome: applicantFormData[FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME],
      currentAnnuityPercentPassedOnDeath: applicantFormData[FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH],
      futureStatePension: applicantFormData[FIELDS.FUTURE_STATE_PENSION],
      futurePensionDefinedBenefits: applicantFormData[FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX].map(
        mapFuturePensionDefinedBenefitToApi,
      ),
      futurePensionDefinedContributions: applicantFormData[FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX].map(
        mapFuturePensionDefinedContributionToApi,
      ),
      futurePensionAnnuity: applicantFormData[FIELDS.FUTURE_ANNUITY_PREFIX].map(mapFutureAnnuityToApi),
      employmentStatus: applicantFormData[FIELDS.APPLICANT_EMPLOYMENT_STATUS],
      employedJobTitle: applicantFormData[FIELDS.APPLICANT_EMPLOYED_JOB_TITLE],
      employedCompanyName: applicantFormData[FIELDS.APPLICANT_EMPLOYED_COMPANY_NAME],
      employedCompanyAddress: applicantFormData[FIELDS.APPLICANT_EMPLOYED_COMPANY_ADDRESS],
      employedNature: applicantFormData[FIELDS.APPLICANT_EMPLOYED_NATURE],
      employedStartDate: applicantFormData[FIELDS.APPLICANT_EMPLOYED_START_DATE]
        ? format(applicantFormData[FIELDS.APPLICANT_EMPLOYED_START_DATE] || '').fromViewToRequest(true)
        : null,
      employedAnticipatedAge: applicantFormData[FIELDS.APPLICANT_EMPLOYED_ANTICIPATED_AGE],
      selfEmployedJobTitle: applicantFormData[FIELDS.APPLICANT_SELF_EMPLOYED_JOB_TITLE],
      selfEmployedCompanyName: applicantFormData[FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_NAME],
      selfEmployedCompanyAddress: applicantFormData[FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_ADDRESS],
      selfEmployedNature: applicantFormData[FIELDS.APPLICANT_SELF_EMPLOYED_NATURE],
      selfEmployedStartDate: applicantFormData[FIELDS.APPLICANT_SELF_EMPLOYED_START_DATE]
        ? format(applicantFormData[FIELDS.APPLICANT_SELF_EMPLOYED_START_DATE] || '').fromViewToRequest(true)
        : null,
      businessType: applicantFormData[FIELDS.APPLICANT_BUSINESS_TYPE],
      selfEmployedAnticipatedAge: applicantFormData[FIELDS.APPLICANT_SELF_EMPLOYED_ANTICIPATED_AGE],
      ...currentIncomeMapper.mapDomainToApi(applicantFormData),
      ...mapFutureIncomeDomainToApi(applicantFormData),
      numberOfDependants: applicantFormData[FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS],
      lumpSumDrawdown: applicantFormData[FIELDS.LUMP_SUM_DRAWDOWN],
      repaymentLumpSum: applicantFormData[FIELDS.REPAYMENT_LUMP_SUM],
    };
  };

  const mapPropertyToApi = (property: CaseDetails['case']['property']): CaseData['property'] => {
    return {
      flatNumber: property[FIELDS.PROPERTY_FLAT_NUMBER],
      buildingName: property[FIELDS.PROPERTY_BUILDING_NAME],
      buildingNumber: property[FIELDS.PROPERTY_BUILDING_NUMBER],
      street: property[FIELDS.PROPERTY_STREET],
      city: property[FIELDS.PROPERTY_CITY],
      postcode: property[FIELDS.PROPERTY_POSTCODE],

      hasOccupantsOver17: property[FIELDS.PROPERTY_HAS_OCCUPANTS_OVER17],

      type: property[FIELDS.PROPERTY_TYPE],
      floorNumber: property[FIELDS.PROPERTY_FLOOR_NUMBER],
      numberOfFloorsInTheBuilding: property[FIELDS.PROPERTY_NUMBER_OF_FLOORS_IN_THE_BUILDING],
      tenureType: property[FIELDS.PROPERTY_TENURE_TYPE],
      remainingYears: property[FIELDS.PROPERTY_REMAINING_YEARS],
      isExLocalAuthority: property[FIELDS.PROPERTY_IS_EX_LOCAL_AUTHORITY],
      isRetirementHome: property[FIELDS.PROPERTY_IS_RETIREMENT_HOME],
      isRestrictedCovenant: property[FIELDS.PROPERTY_IS_RESTRICTED_COVENANT],
      isAboveCommercialPremises: property[FIELDS.PROPERTY_IS_ABOVE_COMMERCIAL_PREMISES],
      commercialPremisesDetails: property[FIELDS.PROPERTY_COMMERCIAL_PREMISES_DETAILS],
      commercialUsageDetails: property[FIELDS.PROPERTY_COMMERCIAL_USAGE_DETAILS],
      numberOfKitchens: property[FIELDS.PROPERTY_NUMBER_OF_KITCHENS],
      numberOfBedrooms: property[FIELDS.PROPERTY_NUMBER_OF_BEDROOMS],
      hasAgriculturalRestrictions: property[FIELDS.PROPERTY_HAS_AGRICULTURAL_RESTRICTIONS],
      propertyYearBuilt: property[FIELDS.PROPERTY_PROPERTY_YEAR_BUILT],
      warrantyProvider: property[FIELDS.PROPERTY_WARRANTY_PROVIDER],
      propertyConstruction: property[FIELDS.PROPERTY_PROPERTY_CONSTRUCTION],
      propertyDetails: property[FIELDS.PROPERTY_PROPERTY_DETAILS],
      valuationType: property[FIELDS.PROPERTY_VALUATION_TYPE],
      titleNumber: property[FIELDS.PROPERTY_TITLE_NUMBER],
      instructionDate: property[FIELDS.PROPERTY_INSTRUCTION_DATE]
        ? format(property[FIELDS.PROPERTY_INSTRUCTION_DATE] || '').fromViewToRequest(true)
        : null,
      inspectionDate: property[FIELDS.PROPERTY_INSPECTION_DATE]
        ? format(property[FIELDS.PROPERTY_INSPECTION_DATE] || '').fromViewToRequest(true)
        : null,
      isPropertyNewBuild: property[FIELDS.PROPERTY_IS_PROPERTY_NEW_BUILD]
        ? logicalMapFrontToBack(property[FIELDS.PROPERTY_IS_PROPERTY_NEW_BUILD] as BooleanString)
        : null,
      hasSpecialistReport: property[FIELDS.PROPERTY_HAS_SPECIALIST_REPORT]
        ? logicalMapFrontToBack(property[FIELDS.PROPERTY_HAS_SPECIALIST_REPORT] as BooleanString)
        : null,
      hasCommercialUsageAbove25Percent: property[FIELDS.PROPERTY_HAS_COMMERCIAL_USAGE_ABOVE25_PERCENT]
        ? logicalMapFrontToBack(property[FIELDS.PROPERTY_HAS_COMMERCIAL_USAGE_ABOVE25_PERCENT] as BooleanString)
        : null,
      hasLiftAccess: property[FIELDS.PROPERTY_HAS_LIFT_ACCESS]
        ? logicalMapFrontToBack(property[FIELDS.PROPERTY_HAS_LIFT_ACCESS] as BooleanString)
        : null,
      isPrimaryHome: property[FIELDS.PROPERTY_IS_PRIMARY_HOME]
        ? logicalMapFrontToBack(property[FIELDS.PROPERTY_IS_PRIMARY_HOME] as BooleanString)
        : null,
      estimatedPropertyValue: property[FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE],
      propertyPurchasePrice: property[FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE],
      actualPropertyValue: property[FIELDS.PROPERTY_ACTUAL_PROPERTY_VALUE],
      occupants: property[FIELDS.PROPERTY_OCCUPANTS_PREFIX].map((occupant) => ({
        id: occupant.id,
        fullName: occupant.fullName,
        dateOfBirth: occupant[FIELDS.PROPERTY_OCCUPANT_DATE_OF_BIRTH]
          ? format(occupant[FIELDS.PROPERTY_OCCUPANT_DATE_OF_BIRTH] || '').fromViewToRequest(true)
          : null,
      })),
    };
  };

  const mapAssurancePolicyToApi = (assurancePolicyForm: FormAssurancePolicy): AssurancePolicyRaw => {
    return {
      id: assurancePolicyForm.id,
      assuredPerson: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_ASSURED_PERSON],
      type: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_TYPE],
      paymentsPerAnnum: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_PAYMENTS_PER_ANNUM],
      basisOfCover: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_BASIS_OF_COVER],
      insuranceLumpSum: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_INSURANCE_LUMP_SUM],
      monthlyPremium: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_MONTHLY_PAYMENT],
      mortgageMonthlyPayments: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_MORTGAGE_MONTHLY_PAYMENTS],
      expiryDate: assurancePolicyForm[FIELDS.ASSURANCE_POLICY_EXPIRY_DATE]
        ? format(assurancePolicyForm[FIELDS.ASSURANCE_POLICY_EXPIRY_DATE] || '').fromViewToRequest(true)
        : assurancePolicyForm[FIELDS.ASSURANCE_POLICY_EXPIRY_DATE],
    };
  };

  const mapSolicitorToApi = (solicitor: FormSolicitor): Solicitor => ({
    solicitorName: solicitor[FIELDS.SOLICITOR_NAME],
    solicitorContactNumber: solicitor[FIELDS.SOLICITOR_CONTACT_NUMBER],
    solicitorEmail: solicitor[FIELDS.SOLICITOR_EMAIL],
    solicitorFlatNumber: solicitor[FIELDS.SOLICITOR_FLAT_NUMBER],
    solicitorBuildingNumber: solicitor[FIELDS.SOLICITOR_BUILDING_NUMBER],
    solicitorBuildingName: solicitor[FIELDS.SOLICITOR_BUILDING_NAME],
    solicitorStreet: solicitor[FIELDS.SOLICITOR_STREET],
    solicitorCity: solicitor[FIELDS.SOLICITOR_ADDRESS_CITY],
    solicitorPostcode: solicitor[FIELDS.SOLICITOR_ADDRESS_POSTCODE],
  });

  const mapAdminToApi = (admin: FormAdmin): Omit<Admin, 'pepperUploadDate'> => ({
    offerDate: admin[FIELDS.ADMIN_OFFER_DATE]
      ? format(admin[FIELDS.ADMIN_OFFER_DATE] || '').fromViewToRequest(true)
      : null,
    cotRecDate: admin[FIELDS.ADMIN_COT_REC_DATE]
      ? format(admin[FIELDS.ADMIN_COT_REC_DATE] || '').fromViewToRequest(true)
      : null,
    fundsReqDate: admin[FIELDS.ADMIN_FUNDS_REQ_DATE]
      ? format(admin[FIELDS.ADMIN_FUNDS_REQ_DATE] || '').fromViewToRequest(true)
      : null,
    expectedCompletionDate: admin[FIELDS.ADMIN_EXPECTED_COMPLETION_DATE]
      ? format(admin[FIELDS.ADMIN_EXPECTED_COMPLETION_DATE] || '').fromViewToRequest(true)
      : null,
    completionDate: admin[FIELDS.ADMIN_COMPLETION_DATE]
      ? format(admin[FIELDS.ADMIN_COMPLETION_DATE] || '').fromViewToRequest(true)
      : null,
    pepperAccountId: admin[FIELDS.ADMIN_PEPPER_ACCOUNT_ID],
    originatorId: admin[FIELDS.ADMIN_ORIGINATOR_ID],
    declineDate: admin[FIELDS.ADMIN_DECLINE_DATE]
      ? format(admin[FIELDS.ADMIN_DECLINE_DATE] || '').fromViewToRequest(true)
      : null,
    swapCost: admin[FIELDS.ADMIN_SWAP_COST],
    expectedMaturity: admin[FIELDS.ADMIN_EXPECTED_MATURITY],
  });

  return {
    ...mapLoanDetailsDomainToApi(formData.loanDetails),
    ...mapSolicitorToApi(formData.contact[FIELDS.SOLICITOR_PREFIX]),
    ...mapAdminToApi(formData.contact[FIELDS.ADMIN_PREFIX]),
    status: formData.status || CaseStatuses.NotSet,
    applicant1: mapApplicantToApi(formData.applicants[0]),
    applicant2: formData.applicants[1] ? mapApplicantToApi(formData.applicants[1]) : null,
    adviserName: formData.contact[FIELDS.ADVISER_NAME],
    adviserFee: formData.contact[FIELDS.ADVISER_FEE],
    adviserPhone: formData.contact[FIELDS.ADVISER_PHONE],
    adviserEmail: formData.contact[FIELDS.ADVISER_EMAIL],
    standardCommissionTerms: formData.contact[FIELDS.STANDARD_COMMISSION_TERMS],
    enhancedCommissionTerms: formData.contact[FIELDS.ENHANCED_COMMISSION_TERMS],
    ongoingCommissionTermsUpfrontFee: formData.contact[FIELDS.ONGOING_COMMISSION_TERMS_UPFRONT_FEE],
    ongoingCommissionTermsOngoingFee: formData.contact[FIELDS.ONGOING_COMMISSION_TERMS_ONGOING_FEE],
    firmName: formData.contact[FIELDS.FIRM_NAME],
    firmFcaNumber: formData.contact[FIELDS.FIRM_FCA_NUMBER],
    firmAddress: formData.contact[FIELDS.FIRM_ADDRESS],
    networkFcaNumber: formData.contact[FIELDS.NETWORK_FCA_NUMBER],
    adviserFeeLastPaymentMoment: formData.contact[FIELDS.ADVISER_FEE_LAST_PAYMENT_MOMENT],
    packagerFee: formData.contact[FIELDS.PACKAGER_FEE],
    packagerFeeLastPaymentMoment: formData.contact[FIELDS.PACKAGER_FEE_LAST_PAYMENT_MOMENT],
    rationale: formData.rationale,
    offerPackUrl: formData.offerPackUrl,
    caseId: formData.identifier,
    property: mapPropertyToApi(formData.property),
    updatedAt: formData.updatedAt,
    policies: formData[FIELDS.ASSURANCE_POLICIES_PREFIX].map(mapAssurancePolicyToApi),
  };
};

export const fieldValueToApi = (field: string, value: any): string => {
  const map: Map<string, (value: any) => any> = new Map<string, (value: any) => any>([
    [FIELDS.IS_CREDIT_POLICY_EXEMPTION.toString(), mapYesNoSelect],
    [FIELDS.HAS_ADVERSE_CREDIT.toString(), mapYesNoSelect],
    [FIELDS.EXPENDITURE_END_DATE.toString(), mapDate],
    [FIELDS.APPLICANT_DATE_OF_BIRTH.toString(), mapDate],
    [FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE.toString(), mapDate],
    [FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE.toString(), mapDate],
    [FIELDS.APPLICANT_EMPLOYED_START_DATE.toString(), mapDate],
    [FIELDS.APPLICANT_ADDRESS_MOVED_IN_DATE.toString(), mapDate],
    [FIELDS.APPLICANT_DEFAULT_LAST_DATE.toString(), mapDate],
    [FIELDS.APPLICANT_CCJ_LATEST_DATE.toString(), mapDate],
    [FIELDS.APPLICANT_IS_CRIMINAL.toString(), mapYesNoSelect],
    [FIELDS.APPLICANT_HAS_ADVERSE_CREDIT.toString(), mapYesNoSelect],
    [FIELDS.PROPERTY_IS_PROPERTY_NEW_BUILD.toString(), mapYesNoSelect],
    [FIELDS.PROPERTY_HAS_SPECIALIST_REPORT.toString(), mapYesNoSelect],
    [FIELDS.START_DATE.toString(), mapDate],
    [FIELDS.PROPERTY_OCCUPANT_DATE_OF_BIRTH.toString(), mapDate],
    [FIELDS.PROPERTY_HAS_COMMERCIAL_USAGE_ABOVE25_PERCENT.toString(), mapYesNoSelect],
    [FIELDS.PROPERTY_HAS_LIFT_ACCESS.toString(), mapYesNoSelect],
    [FIELDS.PROPERTY_IS_PRIMARY_HOME.toString(), mapYesNoSelect],
    [FIELDS.ASSURANCE_POLICY_EXPIRY_DATE.toString(), (value: string) => format(value).fromViewToRequest(true)],
    [FIELDS.OUTSTANDING_MORTGAGE_AMOUNT.toString(), mapNumber],
    [FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS, mapNumber],
    [FIELDS.ANNUAL_COUNCIL_TAX, mapNumber],
    [FIELDS.ANNUAL_GROUND_RENT, mapNumber],
    [
      CURRENT_INCOME_FIELDS.BUSINESS_TRADING_DATE.toString(),
      (value: string) => (value === '' ? null : format(value).fromViewToRequest(true)),
    ],
    [FIELDS.ASSETS_OUTSTANDING_MORTGAGE_BALANCE.toString(), mapNumber],
    [FIELDS.ASSETS_VALUE, mapNumber],
    [FIELDS.APPLICANT_SELF_EMPLOYED_START_DATE, mapDate],
    [FIELDS.APPLICANT_SELF_EMPLOYED_ANTICIPATED_AGE, mapNumber],
    [FIELDS.APPLICANT_EMPLOYED_ANTICIPATED_AGE, mapNumber],
    [FIELDS.PROPERTY_INSPECTION_DATE, mapDate],
    [FIELDS.PROPERTY_ESTIMATED_PROPERTY_VALUE, mapNumber],
    [FIELDS.PROPERTY_PROPERTY_PURCHASE_PRICE, mapNumber],
    [FIELDS.PROPERTY_ACTUAL_PROPERTY_VALUE, mapNumber],
    [FIELDS.PROPERTY_FLOOR_NUMBER, mapNumber],
    [FIELDS.PROPERTY_NUMBER_OF_FLOORS_IN_THE_BUILDING, mapNumber],
    [FIELDS.PROPERTY_REMAINING_YEARS, mapNumber],
    [FIELDS.PROPERTY_NUMBER_OF_KITCHENS, mapNumber],
    [FIELDS.PROPERTY_NUMBER_OF_BEDROOMS, mapNumber],
    [FIELDS.PROPERTY_PROPERTY_YEAR_BUILT, mapNumber],
    [FIELDS.PROPERTY_INSTRUCTION_DATE.toString(), mapDate],
    [FIELDS.FUTURE_PENSION_REPAYMENT_LUMP_SUM, mapNumber],
    [FIELDS.ADMIN_OFFER_DATE, mapDate],
    [FIELDS.ADMIN_COT_REC_DATE, mapDate],
    [FIELDS.ADMIN_FUNDS_REQ_DATE, mapDate],
    [FIELDS.ADMIN_COMPLETION_DATE, mapDate],
    [FIELDS.ADMIN_PEPPER_UPLOAD_DATE, mapDate],
    [FIELDS.ADMIN_DECLINE_DATE, mapDate],
    [FIELDS.ADMIN_SWAP_COST, mapNumber],
    [FIELDS.ADMIN_EXPECTED_MATURITY, mapNumber],
    [FIELDS.RE_INSPECTION_FEE, mapNumber],
    [FIELDS.ASSURANCE_POLICY_MORTGAGE_MONTHLY_PAYMENTS, mapNumber],
    [FIELDS.ASSURANCE_POLICY_MONTHLY_PAYMENT, mapNumber],
    [FIELDS.ASSURANCE_POLICY_PAYMENTS_PER_ANNUM, mapNumber],
    [FIELDS.ASSURANCE_POLICY_EXPIRY_DATE, mapDate],
    [FIELDS.LOAN_AMOUNT_PURCHASE, mapNumber],
    [FIELDS.LOAN_AMOUNT_REMORTGAGE, mapNumber],
    [FIELDS.LOAN_DEBT_CONSOLIDATION, mapNumber],
    [FIELDS.LOAN_HOME_IMPROVEMENTS, mapNumber],
    [FIELDS.LOAN_DISCRETIONARY_SPEND, mapNumber],
    [FIELDS.LOAN_ASSISTING_OTHERS, mapNumber],
    [FIELDS.APPLICANT_BANKRUPTCY_AMOUNT, mapNumber],
    [FIELDS.APPLICANT_CCJ_AMOUNT, mapNumber],
    [FIELDS.APPLICANT_DEFAULT_AMOUNT, mapNumber],
    [FIELDS.APPLICANT_MORTGAGE_ARREARS_AMOUNT, mapNumber],
    [FIELDS.ASSURANCE_POLICY_INSURANCE_LUMP_SUM, mapNumber],
    [FIELDS.ASSURANCE_POLICY_PAYMENTS_PER_ANNUM, mapNumber],
    [FIELDS.CURRENT_STATE_PENSION, mapNumber],
    [FIELDS.CURRENT_DC_CURRENT_VALUE, mapNumber],
    [FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH, mapNumber],
    [FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN, mapNumber],
    [FIELDS.CURRENT_DB_ANNUAL_INCOME, mapNumber],
    [FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH, mapNumber],
    [FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME, mapNumber],
    [FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH, mapNumber],
    [FIELDS.CURRENT_VALUE, mapNumber],
    [FIELDS.FUTURE_STATE_PENSION, mapNumber],
    [FIELDS.ONGOING_MONTHLY_CONTRIBUTIONS, mapNumber],
    [FIELDS.PERCENTAGE_VALUE_OF_PASSED_ON_DEATH, mapNumber],
    [FIELDS.FUTURE_ANNUAL_INCOME_DRAWN, mapNumber],
    [FIELDS.LUMP_SUM_DRAWDOWN, mapNumber],
    [FIELDS.REPAYMENT_LUMP_SUM, mapNumber],
    [FIELDS.ANNUAL_INCOME, mapNumber],
    [FIELDS.PERCENTAGE_VALUE_OF_PASSED_ON_DEATH, mapNumber],
    [FIELDS.FUTURE_PENSION_REPAYMENT_LUMP_SUM, mapNumber],
    [FIELDS.FUTURE_ANNUITY_GROSS_ANNUAL_INCOME, mapNumber],
    [FIELDS.FUTURE_ANNUITY_PERCENT_PASSED_ON_DEATH, mapNumber],
    [FIELDS.SUBMISSION_ROUTE, mapString],
    [FIELDS.COMMISSION_PAID_TO, mapString],
  ]);

  const mapper = map.get(field);

  return mapper ? mapper(value) : value;
};
